/**
 * A player-authored NPC placed in the world.
 *
 * Parallel to `Animal` (creatures/animal.js), with three differences: four-way
 * unique facing instead of a mirrored side pair, a behavior supplied by the
 * global roster, and a feet-fixed standing bob. Wander steering is shared
 * (`engine/wander.js`); both share terrain collision through the actor foot box.
 */

import { FRAME_HEIGHT, FRAME_WIDTH, walkFrame } from '../character/catalog.js';
import {
  BOB_PERIOD_S,
  DEFAULT_BEHAVIOR,
  FACE_DEADBAND_RATIO,
  IDLE_MAX_S,
  IDLE_MIN_S,
  NPC_WALK_SPEED,
  STEP_MAX_TILES,
  STEP_MIN_TILES,
  WAIST_ROW,
  wanderRadiusTiles,
} from './catalog.js';
import { advanceWanderWalk, startWanderWalk } from '../wander.js';

const DIRECTION_ROWS = { down: 0, up: 1, left: 2, right: 3 };
export const DIRECTIONS = ['down', 'up', 'left', 'right'];

/** Skip wander targets closer than this — a sub-step shuffle reads as a glitch. */
const TARGET_MIN_STEP = 0.5;
/** Landing exactly on the rim invites float rounding to read as out of bounds. */
const LEASH_INSET = 1e-9;

export class Npc {
  constructor(record, options = {}) {
    this.npcId = record.npcId;
    this.spawnX = record.spawnX;
    this.spawnY = record.spawnY;
    this.x = Number.isFinite(record.x) ? record.x : record.spawnX;
    this.y = Number.isFinite(record.y) ? record.y : record.spawnY;
    this.direction = DIRECTIONS.includes(record.direction) ? record.direction : 'down';
    this.definition = { behavior: DEFAULT_BEHAVIOR, radiusTiles: wanderRadiusTiles() };
    this.state = 'idle';
    this.stateTime = 0;
    this.walkTime = 0;
    this.animTime = 0;
    this.targetX = this.x;
    this.targetY = this.y;
    this.random = options.random ?? Math.random;
    this.idleDuration = this._rollIdleDuration();
  }

  static fromJSON(record, options = {}) {
    return new Npc(record, options);
  }

  toJSON() {
    // Centi-tile precision keeps saves small; see Animal.toJSON.
    const round = (value) => Math.round(value * 100) / 100;
    return {
      npcId: this.npcId,
      spawnX: round(this.spawnX),
      spawnY: round(this.spawnY),
      x: round(this.x),
      y: round(this.y),
      direction: this.direction,
    };
  }

  /**
   * Point this instance at its roster definition. Called on load and after an
   * edit; deliberately leaves spawnX/spawnY alone so switching Wanderer ->
   * Still and back resumes the leash around the original spawn point.
   */
  setDefinition(npc) {
    this.definition = {
      behavior: npc?.behavior ?? DEFAULT_BEHAVIOR,
      radiusTiles: wanderRadiusTiles(npc?.wanderRadius),
    };
    if (this.definition.behavior !== 'wander' && this.state === 'walk') this._startIdle();
    return this;
  }

  get moving() {
    return this.state === 'walk';
  }

  update(dt, world) {
    if (!(dt > 0)) return;
    this.animTime += dt;
    if (this.definition.behavior !== 'wander') {
      if (this.state === 'walk') this._startIdle();
      return;
    }
    if (this.state === 'idle') {
      this.stateTime += dt;
      if (this.stateTime >= this.idleDuration) this._startWalk(world);
      return;
    }
    advanceWanderWalk(this, world, dt, {
      speed: NPC_WALK_SPEED,
      faceToward: (dx, dy) => this._faceToward(dx, dy),
      rest: () => this._startIdle(),
      onStep: (stepDt) => { this.walkTime += stepDt; },
    });
  }

  draw(ctx, origin, tileSize, image) {
    if (!image) return;
    const frame = this.moving ? walkFrame(this.walkTime, NPC_WALK_SPEED) : 0;
    const row = DIRECTION_ROWS[this.direction];
    const width = tileSize;
    const height = tileSize * 1.5;
    const screenX = Math.round(origin.x + this.x * tileSize - width / 2);
    const screenY = Math.round(origin.y + this.y * tileSize - height * 2 / 3);
    const scale = height / FRAME_HEIGHT;
    const waist = Math.round(WAIST_ROW * scale);
    const sourceX = frame * FRAME_WIDTH;
    const sourceY = row * FRAME_HEIGHT;

    // Legs and feet are drawn at a fixed screen row; only the torso settles, so
    // the feet stay planted (docs/creature-conventions.md 2.1). The settle
    // compresses the torso — shifting it at full height would run its bottom
    // rows under the legs slice and clip them.
    const bob = this.moving ? 0 : this._bobOffset(tileSize);
    ctx.drawImage(
      image,
      sourceX, sourceY, FRAME_WIDTH, WAIST_ROW,
      screenX, screenY + bob, width, waist - bob,
    );
    ctx.drawImage(
      image,
      sourceX, sourceY + WAIST_ROW, FRAME_WIDTH, FRAME_HEIGHT - WAIST_ROW,
      screenX, screenY + waist, width, height - waist,
    );
  }

  /** One sprite pixel of settle on the exhale half of the breathing cycle. */
  _bobOffset(tileSize) {
    const phase = (this.animTime % BOB_PERIOD_S) / BOB_PERIOD_S;
    return phase < 0.5 ? 0 : Math.max(1, Math.round(tileSize / FRAME_WIDTH));
  }

  _rollIdleDuration() {
    return IDLE_MIN_S + this.random() * (IDLE_MAX_S - IDLE_MIN_S);
  }

  _startIdle() {
    this.state = 'idle';
    this.stateTime = 0;
    this.walkTime = 0;
    this.idleDuration = this._rollIdleDuration();
  }

  _startWalk(world) {
    if (!startWanderWalk(this, world, {
      radius: this.definition.radiusTiles,
      stepMin: STEP_MIN_TILES,
      stepMax: STEP_MAX_TILES,
      minDistance: TARGET_MIN_STEP,
      inset: LEASH_INSET,
      faceToward: (dx, dy) => this._faceToward(dx, dy),
    })) {
      this.idleDuration = this._rollIdleDuration();
      this.stateTime = 0;
      return;
    }
    this.walkTime = 0;
  }

  _faceToward(dx, dy) {
    const distance = Math.hypot(dx, dy);
    if (distance === 0) return;
    // A near-45° step would otherwise flip the facing whenever |dx| and |dy|
    // trade places mid-walk; inside the deadband the current facing wins.
    if (Math.abs(Math.abs(dx) - Math.abs(dy)) / distance < FACE_DEADBAND_RATIO) return;
    if (Math.abs(dx) > Math.abs(dy)) this.direction = dx < 0 ? 'left' : 'right';
    else this.direction = dy < 0 ? 'up' : 'down';
  }
}
